// Pixel-hides a numeric value in place. Icons and units stay as siblings —
// only [data-pix] is covered. Cells are visible mid-darks, no gaps: a near-
// black smear on #0d0d0f reads as "the number vanished", not as a hide.

import { type ReactNode, useEffect, useRef } from 'react'
import styles from './PixelValue.module.css'

const TONE = ['#1a1a1f', '#222228', '#2a2a32', '#32323c', '#3c3c48'] as const

/** A dash is not a balance — do not pixel-hide empty tiles. */
export function hasHideableBalance(stamp: string): boolean {
  return /\d/.test(stamp)
}

function glyphBoxes(host: HTMLElement): Array<{ x: number; y: number; w: number; h: number }> {
  const box = host.getBoundingClientRect()
  const marks = host.querySelectorAll<HTMLElement>('[data-pix]')
  const out: Array<{ x: number; y: number; w: number; h: number }> = []
  const add = (el: HTMLElement) => {
    const r = el.getBoundingClientRect()
    const size = Number.parseFloat(getComputedStyle(el).fontSize) || 16
    const h = Math.max(8, Math.round(size * 0.7))
    out.push({
      x: r.left - box.left,
      y: r.top - box.top + Math.max(0, (r.height - h) / 2),
      w: Math.max(4, r.width),
      h,
    })
  }
  if (marks.length > 0) {
    for (const el of marks) add(el)
    return out
  }
  add(host)
  return out
}

function paintHide(canvas: HTMLCanvasElement, host: HTMLElement, grain: number) {
  const box = host.getBoundingClientRect()
  const w = Math.max(Math.round(box.width), 4)
  const h = Math.max(Math.round(box.height), 4)
  const dpr = Math.max(1, window.devicePixelRatio || 1)
  canvas.width = Math.round(w * dpr)
  canvas.height = Math.round(h * dpr)
  canvas.style.width = `${w}px`
  canvas.style.height = `${h}px`
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.clearRect(0, 0, w, h)
  const step = grain
  for (const t of glyphBoxes(host)) {
    const cols = Math.max(1, Math.round(t.w / step))
    const rows = Math.max(1, Math.round(t.h / step))
    const cellW = t.w / cols
    const cellH = t.h / rows
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const hash = (x * 17 + y * 31) % TONE.length
        ctx.fillStyle = TONE[hash] ?? TONE[0]
        ctx.fillRect(t.x + x * cellW, t.y + y * cellH, cellW + 0.4, cellH + 0.4)
      }
    }
  }
}

export function PixelValue({
  masked,
  stamp,
  grain = 'md',
  children,
}: {
  masked: boolean
  stamp: string
  /** lg = the Total Value figure; md = compact row amounts. */
  grain?: 'md' | 'lg'
  children: ReactNode
}) {
  const host = useRef<HTMLSpanElement>(null)
  const canvas = useRef<HTMLCanvasElement>(null)
  const on = masked && hasHideableBalance(stamp)
  const step = grain === 'lg' ? 8 : 6

  useEffect(() => {
    const h = host.current
    const c = canvas.current
    if (!h || !c) return
    const paint = () => {
      void stamp
      if (on) paintHide(c, h, step)
      else {
        const ctx = c.getContext('2d')
        ctx?.clearRect(0, 0, c.width, c.height)
      }
    }
    const raf = requestAnimationFrame(paint)
    const ro = new ResizeObserver(() => {
      if (on) paintHide(c, h, step)
    })
    ro.observe(h)
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
    }
  }, [on, stamp, step])

  return (
    <span ref={host} className={styles.host} data-on={on || undefined}>
      <span className={styles.src} data-gone={on || undefined}>
        {children}
      </span>
      <canvas ref={canvas} className={styles.canvas} aria-hidden />
    </span>
  )
}
